import { useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { TaskState, TaskWithRelations } from "@/hooks/useTasks";
import { TaskCard } from "./TaskCard";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";

interface TaskColumnProps {
  personId: string;
  personName: string;
  tasksByState: Record<TaskState, TaskWithRelations[]>;
  onDragStart: (taskId: string) => void;
  onDrop: (state: TaskState) => void;
  onEditTask: (task: TaskWithRelations) => void;
  onCreateTask: (personId: string, state?: TaskState) => void;
  isDragging: boolean;
  isLast: boolean;
}

const STATE_CONFIG: { state: TaskState; label: string; color: string }[] = [
  { state: "backlog", label: "Backlog", color: "text-muted-foreground" },
  { state: "todo", label: "To Do", color: "text-blue-600" },
  { state: "in_progress", label: "In Progress", color: "text-amber-600" },
  { state: "done", label: "Done", color: "text-green-600" },
];

export function TaskColumn({
  personId,
  personName,
  tasksByState,
  onDragStart,
  onDrop,
  onEditTask,
  onCreateTask,
  isDragging,
  isLast,
}: TaskColumnProps) {
  const [expandedStates, setExpandedStates] = useState<
    Record<TaskState, boolean>
  >({
    backlog: true,
    todo: true,
    in_progress: true,
    done: false,
  });

  const toggleState = (state: TaskState) => {
    setExpandedStates((prev) => ({
      ...prev,
      [state]: !prev[state],
    }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, state: TaskState) => {
    e.preventDefault();
    onDrop(state);
  };

  const totalTasks = Object.values(tasksByState).reduce(
    (sum, tasks) => sum + tasks.length,
    0,
  );

  return (
    <div
      className={cn(
        "flex h-full w-80 max-w-80 flex-none flex-col",
        !isLast && "border-r border-border",
      )}
    >
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between gap-2">
          <h3 className="min-w-0 truncate font-semibold">{personName}</h3>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onCreateTask(personId)}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">{totalTasks}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
        {STATE_CONFIG.map(({ state, label, color }) => {
          const tasks = tasksByState[state] || [];
          const isExpanded = expandedStates[state];

          return (
            <Collapsible
              key={state}
              open={isExpanded}
              onOpenChange={() => toggleState(state)}
            >
              <div
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, state)}
                className={cn(
                  "rounded-lg transition-colors",
                  isDragging && "ring-1 ring-primary/30 bg-muted/30",
                )}
              >
                <div className="group/state-header flex items-center gap-1 rounded px-2 -mx-2 hover:bg-muted/50">
                  <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-2 py-2 text-left">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className={cn("text-sm font-medium", color)}>
                      {label}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {tasks.length}
                    </span>
                  </CollapsibleTrigger>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 transition-opacity group-hover/state-header:opacity-100 focus-visible:opacity-100"
                    onClick={() => onCreateTask(personId, state)}
                    aria-label={`Create ${label} task for ${personName}`}
                    offlineEdit
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <CollapsibleContent>
                  <div className="space-y-2 pt-1 pb-2">
                    {tasks.length > 0 ? (
                      tasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onDragStart={onDragStart}
                          onClick={() => onEditTask(task)}
                        />
                      ))
                    ) : (
                      <div className="text-xs text-muted-foreground py-2 text-center">
                        No tasks
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
}
